import {Outlet, useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {ContentArea} from '@/components/shared/ContentArea'
import {Users} from 'lucide-react'
import {useAuth} from '@/contexts/auth-context'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {InlineRow} from '@/components/shared/InlineRow'
import {SidebarNav, type SidebarNavItem} from '@/components/layout/SidebarNav'
import {SidebarNavId} from '@/testids/SidebarNavTestIds'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import {CONCEPT_ICON, type ConceptKey} from '@/lib/concepts'
import {EmptyState} from '@/components/shared/EmptyState'
import {SectionContainer} from '@/components/shared/SectionContainer'
import {usePromotedTypes, promotedFor, pickPromotedLabel, PROMOTABLE_ENTITY_TABLES, PROMOTED_TESTID_PREFIX, BY_TYPE_PATH_SEGMENT} from '@/hooks/use-promoted-types'
import {PromotedTypeSection, type PromotedTypeView} from '@/types/types'


function buildPromotedHref(entry: PromotedTypeView): string {
    if (entry.entityTable === PROMOTABLE_ENTITY_TABLES.PARTY_TYPES) {
        return routes.identityManagement.parties.byType(entry.code)
    }
    return routes.identityManagement.identities.byType(entry.code)
}

function resolveRequiredPermission(entry: PromotedTypeView): string {
    return entry.entityTable === PROMOTABLE_ENTITY_TABLES.PARTY_TYPES
        ? permissions.parties.view
        : permissions.identities.view
}

function buildPromotedSidebarItem(
    entry: PromotedTypeView,
    currentLang: string,
    defaultDescription: string,
): SidebarNavItem & { permission: string } {
    const labelText = pickPromotedLabel(entry, currentLang)
    const conceptKey = entry.icon as ConceptKey | undefined
    const fallbackIcon = entry.entityTable === PROMOTABLE_ENTITY_TABLES.PARTY_TYPES
        ? CONCEPT_ICON.party
        : CONCEPT_ICON.identity
    const icon = conceptKey && CONCEPT_ICON[conceptKey] ? CONCEPT_ICON[conceptKey] : fallbackIcon
    return {
        id: `${PROMOTED_TESTID_PREFIX}${entry.entityTable}-${entry.code.toLowerCase()}`,
        title: labelText,
        href: buildPromotedHref(entry),
        icon,
        description: defaultDescription,
        permission: resolveRequiredPermission(entry),
    }
}

export function IdentityManagement() {
    const i18n = useI18n()
    usePageTitle(i18n.identityManagement.title)
    const location = useLocation()
    const {hasPermission} = useAuth()
    const {i18n: i18nInstance} = useTranslation()
    const lang = i18nInstance.language
    const {promoted} = usePromotedTypes()
    const isRoot = location.pathname === routes.identityManagement.root()

    const menuItems: (SidebarNavItem & { permission: string })[] = [
        {
            id: SidebarNavId.Parties,
            title: i18n.identityManagement.parties,
            href: routes.identityManagement.parties.list(),
            icon: CONCEPT_ICON.party,
            description: i18n.parties.menuDescription,
            permission: permissions.parties.view,
            excludePathContains: BY_TYPE_PATH_SEGMENT,
        },
        {
            id: SidebarNavId.Identities,
            title: i18n.identityManagement.identities,
            href: routes.identityManagement.identities.list(),
            icon: CONCEPT_ICON.identity,
            description: i18n.identities.menuDescription,
            permission: permissions.identities.view,
            excludePathContains: BY_TYPE_PATH_SEGMENT,
        },
        {
            id: SidebarNavId.Accounts,
            title: i18n.identityManagement.accounts,
            href: routes.identityManagement.accounts.path(),
            icon: CONCEPT_ICON.account,
            description: i18n.accounts.menuDescription,
            permission: permissions.accounts.view,
        },
        {
            id: SidebarNavId.Onboarding,
            title: i18n.onboarding.title,
            href: routes.identityManagement.onboarding.create(),
            icon: CONCEPT_ICON.onboarding,
            description: i18n.onboarding.menuDescription,
            permission: permissions.onboarding.create,
        },
    ]

    const promotedItems: (SidebarNavItem & { permission: string })[] = promotedFor(promoted, PromotedTypeSection.IDENTITY_MANAGEMENT)
        .map((entry) => buildPromotedSidebarItem(entry, lang, i18n.parties.menuDescription))

    const visibleMenuItems = [...menuItems, ...promotedItems].filter(item => hasPermission(item.permission))

    return (
        <SectionContainer>
            <InlineRow align="start">
                <SidebarNav items={visibleMenuItems} aria-label={String(i18n.identityManagement.title)}/>

                {/* Content Area */}
                <ContentArea>
                    {isRoot ? (
                        <EmptyState
                            variant="card"
                            icon={Users}
                            title={String(i18n.identityManagement.title)}
                            description={String(i18n.identityManagement.selectOption)}
                            data-testid="identity-management-welcome-card"
                        />
                    ) : (
                        <Outlet/>
                    )}
                </ContentArea>
            </InlineRow>
        </SectionContainer>
    )
}
